// Buat Dokumen, the guided generator. Choosing a document reads like
// choosing a prompt: every option is a row with its label and one line of
// description, one tap selects and advances.
//   step_rail         plain spacing   numbered steps 1 to 4, the choice made or a short hint, Ubah (list text rows)
//   output_preview    card            once jenis is chosen: the outline the AI will write and the knowledge it
//                                     draws on; in the rail from 1024px, an expandable panel below (core.list_text)
//   selection_step    section         option rows (core.list_item_button + one list entrance, tier 1)
//   fields_step       card            dynamic catalog fields + Instruksi tambahan untuk AI + Generate
//   generating_state  section         linear indeterminate progress, elapsed seconds, 502 recovery
//   back_action       step head       Kembali, a secondary button above the heading of every step after the
//                                     first, so it is in view without scrolling past the list on any width
// Choices live in the URL (?t, ?j, ?s): every pick pushes one entry, so the
// browser Back steps back one choice. Kembali goes back through history when
// the previous entry is the previous step, otherwise it replaces the current
// entry (design review back_or_replace, 0.96), so the two never disagree. A step
// decided for the user (one tahap, one sub kategori) is still a step Kembali
// returns to: once stepped back to, it waits for a pick instead. The typed
// fields live on this screen, not in the step, so stepping back and forth
// keeps them; a different jenis keeps only the fields it also has.
import { useEffect, useId, useMemo, useRef, useState, type Dispatch, type FormEvent, type ReactNode, type SetStateAction } from "react";
import * as API from "../api";
import { useUser } from "../auth";
import { takePrefill } from "../chat/documents";
import { loadCatalog } from "../lib/catalog";
import { digitsOnly } from "../lib/format";
import { KNOWLEDGE_CATEGORY_LABEL } from "../lib/labels";
import { navigate, useLocation } from "../router";
import { Field, fieldAria, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { GenerationError, GenerationProgress, useElapsed } from "../ui/generation";
import { Glyph } from "../ui/icons";

export type StepId = "tahap" | "jenis" | "sub" | "isi";
export const STEPS: StepId[] = ["tahap", "jenis", "sub", "isi"];

/** The contract document type plus the optional output outline (docs/contract.md). */
export type DocTypeWithOutline = API.CatalogDocType & { outline?: string[] };

export interface Selection {
  tahap?: API.CatalogTahap;
  docType?: DocTypeWithOutline;
  sub?: { id: string; label: string };
}

export function selectionFrom(catalog: API.CatalogTahap[], query: URLSearchParams): Selection {
  const tahap = catalog.find((t) => t.id === query.get("t"));
  const docType = tahap?.documentTypes.find((d) => d.id === query.get("j"));
  const sub = docType?.subCategories.find((s) => s.id === query.get("s"));
  return { tahap, docType, sub };
}

export function currentStep(sel: Selection): StepId {
  if (!sel.tahap) return "tahap";
  if (!sel.docType) return "jenis";
  if (!sel.sub) return "sub";
  return "isi";
}

export function stepLead(step: StepId, rest?: string): string {
  const n = STEPS.indexOf(step) + 1;
  return rest ? `Langkah ${n} dari ${STEPS.length}. ${rest}` : `Langkah ${n} dari ${STEPS.length}.`;
}

/** Knowledge the AI draws on for a document type: files for this type first, then general files for its tahap. */
export function sourcesFor(files: API.KnowledgeFile[], tahap: API.Tahap, docType: string): API.KnowledgeFile[] {
  const specific = files.filter((f) => (f.documentTypes ?? []).includes(docType));
  const general = files.filter(
    (f) => (f.documentTypes ?? []).length === 0 && ((f.tahap ?? []).length === 0 || (f.tahap ?? []).includes(tahap)),
  );
  return [...specific, ...general].slice(0, 5);
}

export function hrefWith(query: URLSearchParams, set: Record<string, string | null>): string {
  const next = new URLSearchParams(query);
  for (const [k, v] of Object.entries(set)) {
    if (v === null) next.delete(k);
    else next.set(k, v);
  }
  const qs = next.toString();
  return qs ? `/buat?${qs}` : "/buat";
}

/** The step Kembali returns to: the one before the current step, null on the first. */
export function previousStep(sel: Selection): StepId | null {
  const at = STEPS.indexOf(currentStep(sel));
  return at > 0 ? STEPS[at - 1]! : null;
}

/** The address of `step`, with every later choice taken out of the query. */
export function stepHref(query: URLSearchParams, step: StepId): string {
  if (step === "tahap") return hrefWith(query, { t: null, j: null, s: null });
  if (step === "jenis") return hrefWith(query, { j: null, s: null });
  if (step === "sub") return hrefWith(query, { s: null });
  return hrefWith(query, {});
}

/** The filled values that still apply to `fields`: known keys, digits for numbers, a listed option for selects. */
export function pruneValues(fields: API.CatalogField[], values: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) {
    const v = values[f.key];
    if (v === undefined || v === "") continue;
    if (f.type === "number" && !/^\d+$/.test(v.trim())) continue;
    if (f.type === "select" && f.options && !f.options.some((o) => o.value === v)) continue;
    out[f.key] = v;
  }
  return out;
}

/** What the user typed on the isian step: kept while they step back and forth. */
export interface Draft {
  title: string;
  values: Record<string, string>;
  instructions: string;
}

const EMPTY_DRAFT: Draft = { title: "", values: {}, instructions: "" };

const BACK_TO: Record<StepId, string> = {
  tahap: "Kembali ke pilihan tahap",
  jenis: "Kembali ke pilihan jenis dokumen",
  sub: "Kembali ke pilihan sub kategori",
  isi: "Kembali ke isian",
};

function BackButton({ to, onBack }: { to: StepId; onBack: () => void }) {
  return (
    <button type="button" className="btn btn-secondary gen-back" aria-label={BACK_TO[to]} onClick={onBack}>
      <Glyph name="arrowLeft" />
      <span>Kembali</span>
    </button>
  );
}

export function GenerateScreen() {
  useTitle("Buat Dokumen");
  const user = useUser();
  const loc = useLocation();
  const catalog = useAsync(() => loadCatalog(user.id), [user.id]);
  const kb = useAsync(() => API.getKnowledge().catch(() => null), [user.id]);

  const allowed = useMemo(() => (catalog.data ?? []).filter((t) => t.roles.includes(user.role)), [catalog.data, user.role]);
  const sel = useMemo(() => selectionFrom(allowed, loc.query), [allowed, loc.query]);
  const step = currentStep(sel);

  // The later choices made before stepping back stay marked as chosen until a
  // different pick makes them invalid.
  const memo = useRef<{ t?: string; j?: string; s?: string }>({});
  if (sel.tahap) memo.current.t = sel.tahap.id;
  if (sel.docType) memo.current.j = sel.docType.id;
  if (sel.sub) memo.current.s = sel.sub.id;

  // Where each address was reached from by a pick on this screen, so Kembali
  // can go back through history instead of piling up entries.
  const cameFrom = useRef(new Map<string, string>());
  const go = (href: string) => {
    cameFrom.current.set(href, loc.href);
    navigate(href);
  };
  const advanceInPlace = (href: string) => {
    const from = cameFrom.current.get(loc.href);
    if (from) cameFrom.current.set(href, from);
    navigate(href, { replace: true });
  };

  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);

  // Steps the user went back to with Kembali: shown and waiting for a pick,
  // even when they have one option.
  const held = useRef(new Set<StepId>());

  // A step with exactly one option is decided for the user.
  useEffect(() => {
    if (!catalog.data || held.current.has(step)) return;
    if (step === "tahap" && allowed.length === 1) advanceInPlace(hrefWith(loc.query, { t: allowed[0]!.id }));
    else if (step === "sub" && sel.docType && sel.docType.subCategories.length === 1) advanceInPlace(hrefWith(loc.query, { s: sel.docType.subCategories[0]!.id }));
    // advanceInPlace reads the current location; the step and query drive this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog.data, step, allowed, sel.docType, loc.query]);

  // Ubah di Buat Dokumen (Asisten AI): the field values wait in sessionStorage,
  // read once for this jenis dokumen and removed at once; never in the URL.
  const prefilled = useRef(false);
  useEffect(() => {
    if (!catalog.data || prefilled.current) return;
    prefilled.current = true;
    const p = takePrefill(sel.docType?.id);
    if (!p || !sel.docType) return;
    setDraft({ title: p.title, values: pruneValues(sel.docType.fields, p.input), instructions: p.instructions });
  }, [catalog.data, sel.docType]);

  const pickTahap = (id: string) => {
    if (id !== memo.current.t) memo.current = { t: id };
    go(hrefWith(loc.query, { t: id, j: null, s: null }));
  };
  const pickJenis = (id: string) => {
    if (id !== memo.current.j) {
      // A different jenis: its own sub kategori, and only the fields it also has.
      memo.current = { t: memo.current.t, j: id };
      const next = sel.tahap?.documentTypes.find((d) => d.id === id);
      setDraft((d) => ({ ...d, values: next ? pruneValues(next.fields, d.values) : {} }));
    }
    go(hrefWith(loc.query, { j: id, s: null }));
  };
  const pickSub = (id: string) => go(hrefWith(loc.query, { s: id }));

  const back = catalog.data ? previousStep(sel) : null;
  const goBack = () => {
    if (!back) return;
    held.current.add(back);
    const to = stepHref(loc.query, back);
    if (cameFrom.current.get(loc.href) === to) history.back();
    else navigate(to, { replace: true });
  };
  const backButton = back ? <BackButton to={back} onBack={goBack} /> : null;

  const sources = sel.tahap && sel.docType && kb.data ? sourcesFor(kb.data.files, sel.tahap.id, sel.docType.id) : null;
  const chosenT = loc.query.get("t") ?? memo.current.t ?? null;
  const chosenJ = loc.query.get("j") ?? (memo.current.j && sel.tahap?.documentTypes.some((d) => d.id === memo.current.j) ? memo.current.j : null);
  const chosenS = loc.query.get("s") ?? (memo.current.s && sel.docType?.subCategories.some((x) => x.id === memo.current.s) ? memo.current.s : null);

  return (
    <>
      <PageHeader
        title="Buat Dokumen"
        lead="Pilih jenis dokumen seperti memilih templat. Pluto menyusun draf, Anda yang memeriksa dan memutuskan."
      />
      {catalog.loading ? (
        <SkeletonRows count={4} label="Memuat katalog dokumen" />
      ) : catalog.error ? (
        <Notice tone="danger" title="Katalog dokumen belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={catalog.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(catalog.error)}</p>
        </Notice>
      ) : allowed.length === 0 ? (
        <Notice tone="info" title="Tidak ada tahap untuk peran Anda">
          <p>Peran Anda tidak membuat dokumen. Anda tetap dapat membaca dokumen.</p>
        </Notice>
      ) : (
        <div className="gen-layout">
          <StepRail sel={sel} step={step} query={loc.query} tahapCount={allowed.length} sources={sources} />
          <div className="gen-main">
            {step === "tahap" ? (
              <OptionStep
                key="tahap"
                title="Pilih tahap pengadaan"
                lead={stepLead("tahap", "Hanya tahap yang boleh Anda buat yang ditampilkan.")}
                options={allowed.map((t) => ({ id: t.id, label: t.label, description: t.description }))}
                selected={chosenT}
                onPick={pickTahap}
              />
            ) : step === "jenis" ? (
              <OptionStep
                key={`jenis-${sel.tahap!.id}`}
                title="Pilih jenis dokumen"
                lead={stepLead("jenis", sel.tahap!.label)}
                options={sel.tahap!.documentTypes.map((d) => ({ id: d.id, label: d.label, description: d.description }))}
                selected={chosenJ}
                onPick={pickJenis}
                back={backButton}
              />
            ) : step === "sub" ? (
              <OptionStep
                key={`sub-${sel.docType!.id}`}
                title="Pilih sub kategori"
                lead={stepLead("sub", sel.docType!.label)}
                options={sel.docType!.subCategories.map((x) => ({ id: x.id, label: x.label }))}
                selected={chosenS}
                onPick={pickSub}
                back={backButton}
              />
            ) : (
              <FieldsStep key={`${sel.docType!.id}-${sel.sub!.id}`} sel={sel} draft={draft} setDraft={setDraft} back={backButton} />
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// The numbered rail: the choice made, or a short hint for what comes
// ---------------------------------------------------------------------------
const HINTS: Record<StepId, string> = {
  tahap: "Pilih tahap pengadaan",
  jenis: "Dokumen yang disusun",
  sub: "Jenis pengadaan atau bentuknya",
  isi: "Data dan instruksi untuk Pluto",
};

function StepRail({
  sel,
  step,
  query,
  tahapCount,
  sources,
}: {
  sel: Selection;
  step: StepId;
  query: URLSearchParams;
  tahapCount: number;
  sources: API.KnowledgeFile[] | null;
}) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rows: { id: StepId; label: string; value: string | null; href: string | null }[] = [
    { id: "tahap", label: "Tahap", value: sel.tahap?.label ?? null, href: tahapCount > 1 ? stepHref(query, "tahap") : null },
    { id: "jenis", label: "Jenis dokumen", value: sel.docType?.label ?? null, href: stepHref(query, "jenis") },
    {
      id: "sub",
      label: "Sub kategori",
      value: sel.sub?.label ?? null,
      href: sel.docType && sel.docType.subCategories.length > 1 ? stepHref(query, "sub") : null,
    },
    { id: "isi", label: "Isian untuk Pluto", value: null, href: null },
  ];
  const at = STEPS.indexOf(step);
  const docType = sel.docType;
  return (
    <aside className="gen-rail" aria-label="Langkah pembuatan dokumen">
      <ol className="summary-list">
        {rows.map((r, i) => {
          const done = i < at;
          const active = i === at;
          return [
            <li key={r.id} className="summary-row" aria-current={active ? "step" : undefined} data-state={done ? "done" : active ? "active" : "todo"}>
              <span className="summary-num num" aria-hidden="true">
                {i + 1}
              </span>
              <span className="summary-text">
                <span className="summary-label">{r.label}</span>
                <span className="summary-value">{done ? r.value : HINTS[r.id]}</span>
              </span>
              {done && r.href ? (
                <a className="btn btn-ghost summary-change" href={r.href} aria-label={`Ubah ${r.label.toLowerCase()}`}>
                  Ubah
                </a>
              ) : null}
            </li>,
            r.id === "jenis" && done && docType ? (
              <li key="preview" className="summary-preview">
                <button type="button" className="btn btn-secondary preview-toggle" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
                  <Glyph name="eye" />
                  <span>{open ? "Tutup pratinjau isi" : "Lihat isi yang akan disusun"}</span>
                </button>
                <div id={panelId} hidden={!open}>
                  <OutputPreview docType={docType} sources={sources} />
                </div>
              </li>
            ) : null,
          ];
        })}
      </ol>
      {docType ? (
        <div className="rail-preview">
          <OutputPreview docType={docType} sources={sources} />
        </div>
      ) : null}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// What the AI will produce: the outline and the knowledge it draws on
// ---------------------------------------------------------------------------
export function OutputPreview({ docType, sources }: { docType: DocTypeWithOutline; sources: API.KnowledgeFile[] | null }) {
  const outline = docType.outline ?? [];
  return (
    <section className="card output-preview" aria-label={`Pratinjau isi ${docType.label}`}>
      <div className="preview-block">
        <h3 className="card-subtitle">Yang akan disusun Pluto</h3>
        {outline.length > 0 ? (
          <ol className="preview-outline">
            {outline.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ol>
        ) : (
          <p className="preview-note">Susunan bagian mengikuti templat {docType.label} dan rujukan di bawah.</p>
        )}
      </div>
      <div className="preview-block">
        <h3 className="card-subtitle">Rujukan yang dipakai</h3>
        {sources === null ? (
          <p className="preview-note">Daftar rujukan belum dapat dimuat.</p>
        ) : sources.length === 0 ? (
          <p className="preview-note">Belum ada berkas khusus untuk dokumen ini. Pluto memakai regulasi umum yang dimuat.</p>
        ) : (
          <ul className="preview-sources">
            {sources.map((s) => (
              <li key={s.id}>
                <span className="preview-source-title">{s.title}</span>
                <span className="row-meta">{KNOWLEDGE_CATEGORY_LABEL[s.category] ?? s.category}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// One selection step: option rows, one tap selects and advances
// ---------------------------------------------------------------------------
export interface Option {
  id: string;
  label: string;
  description?: string;
}

export function OptionStep({
  title,
  lead,
  options,
  selected,
  onPick,
  back,
}: {
  title: string;
  lead: ReactNode;
  options: Option[];
  selected: string | null;
  onPick: (id: string) => void;
  /** Kembali, above the step heading. */
  back?: ReactNode;
}) {
  return (
    <section className="step" aria-labelledby="step-title">
      {back ? <div className="step-back">{back}</div> : null}
      <div className="section-head">
        <div className="section-head-text">
          <h2 id="step-title" className="section-title">
            {title}
          </h2>
          <p className="section-lead">{lead}</p>
        </div>
      </div>
      {/* One entrance for the list (tier 1): the rows move together, so the
          clipped list never holds a row still on its way in. */}
      <ul className="options enter" aria-labelledby="step-title">
        {options.map((o) => (
          <li key={o.id}>
            <button type="button" className="option" aria-pressed={selected === o.id} onClick={() => onPick(o.id)} data-has-desc={o.description ? "" : undefined}>
              <span className="option-text">
                <span className="option-label">{o.label}</span>
                {o.description ? <span className="option-desc">{o.description}</span> : null}
              </span>
              <span className="option-mark" aria-hidden="true">
                <Glyph name={selected === o.id ? "check" : "chevronRight"} />
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Dynamic fields, instructions, Generate
// ---------------------------------------------------------------------------
export function validateFields(fields: API.CatalogField[], values: Record<string, string>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const f of fields) {
    const v = (values[f.key] ?? "").trim();
    if (f.required && !v) errors[f.key] = `${f.label} wajib diisi.`;
    else if (v && f.type === "number" && !/^\d+$/.test(v)) errors[f.key] = "Isi dengan angka saja.";
    else if (v && f.type === "select" && f.options && !f.options.some((o) => o.value === v)) errors[f.key] = "Pilih salah satu pilihan.";
  }
  return errors;
}

type Phase = { kind: "form" } | { kind: "generating" } | { kind: "failed"; error: unknown };

function FieldsStep({ sel, draft, setDraft, back }: { sel: Selection; draft: Draft; setDraft: Dispatch<SetStateAction<Draft>>; back: ReactNode }) {
  const docType = sel.docType!;
  const { title, instructions } = draft;
  const values = useMemo(() => pruneValues(docType.fields, draft.values), [docType.fields, draft.values]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<Phase>({ kind: "form" });
  const seconds = useElapsed(phase.kind === "generating");

  const suggested = docType.label;

  async function run() {
    setPhase({ kind: "generating" });
    try {
      const doc = await API.generateDocument({
        tahap: sel.tahap!.id,
        docType: docType.id,
        subCategory: sel.sub!.id,
        title: title.trim() || undefined,
        input: Object.fromEntries(Object.entries(values).filter(([, v]) => v.trim() !== "")),
        instructions: instructions.trim() || undefined,
      });
      navigate(`/dokumen/${doc.id}`);
    } catch (error) {
      if (error instanceof API.ApiError && (error.status === 400 || error.status === 422)) {
        setPhase({ kind: "form" });
        setErrors({ _form: error.message });
      } else {
        setPhase({ kind: "failed", error });
      }
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    const found = validateFields(docType.fields, values);
    setErrors(found);
    const first = docType.fields.find((f) => found[f.key]);
    if (first) {
      document.getElementById(`f-${first.key}`)?.focus();
      return;
    }
    void run();
  }

  if (phase.kind === "generating") {
    return (
      <section className="step" aria-label="Menyusun draf">
        <GenerationProgress what={docType.label} seconds={seconds} outline={docType.outline} />
      </section>
    );
  }

  if (phase.kind === "failed") {
    return (
      <section className="step" aria-label={`Draf ${docType.label} belum jadi`}>
        <GenerationError
          error={phase.error}
          onRetry={() => void run()}
          secondary={
            <button type="button" className="btn btn-secondary" onClick={() => setPhase({ kind: "form" })}>
              Ubah isian
            </button>
          }
        />
      </section>
    );
  }

  const required = docType.fields.some((f) => f.required);
  const setValue = (key: string, v: string) => setDraft((d) => ({ ...d, values: { ...d.values, [key]: v } }));
  const setTitle = (v: string) => setDraft((d) => ({ ...d, title: v }));
  const setInstructions = (v: string) => setDraft((d) => ({ ...d, instructions: v }));
  const short = docType.fields.filter((f) => f.type !== "textarea");
  const long = docType.fields.filter((f) => f.type === "textarea");

  const titleField = (
    <Field key="f-title" id="f-title" label="Judul dokumen" hint="Kosongkan untuk memakai judul otomatis.">
      <input {...fieldAria("f-title")} value={title} onChange={(e) => setTitle(e.currentTarget.value)} placeholder={suggested} maxLength={200} />
    </Field>
  );
  const instructionsField = (
    <Field key="f-instructions" id="f-instructions" label="Instruksi tambahan untuk Pluto" hint="Opsional. Contoh: gunakan bahasa formal dan sertakan tabel rincian biaya.">
      <textarea {...fieldAria("f-instructions")} rows={4} value={instructions} onChange={(e) => setInstructions(e.currentTarget.value)} maxLength={2000} />
    </Field>
  );
  const shortNodes = [titleField, ...short.map((f) => <DynamicField key={f.key} field={f} value={values[f.key] ?? ""} error={errors[f.key]} onChange={(v) => setValue(f.key, v)} />)];
  const longNodes = [...long.map((f) => <DynamicField key={f.key} field={f} value={values[f.key] ?? ""} error={errors[f.key]} onChange={(v) => setValue(f.key, v)} />), instructionsField];

  return (
    <section className="card step-card" aria-labelledby="fields-title">
      {back ? <div className="step-back">{back}</div> : null}
      <div className="section-head">
        <div className="section-head-text">
          <h2 id="fields-title" className="section-title">
            Isi data {docType.label}
          </h2>
          <p className="section-lead">{stepLead("isi", `${sel.sub!.label}${required ? ". Tanda * wajib diisi." : "."}`)}</p>
        </div>
      </div>
      {errors._form ? (
        <Notice tone="danger" title="Isian belum diterima">
          <p>{errors._form}</p>
        </Notice>
      ) : null}
      <form className="form-stack" noValidate onSubmit={submit}>
        <FieldGrid nodes={shortNodes} />
        <FieldGrid nodes={longNodes} />
        <div className="button-row">
          <button type="submit" className="btn">
            <Glyph name="send" />
            <span>Buat draf dengan Pluto</span>
          </button>
        </div>
        <p className="form-note">Penyusunan biasanya 10 sampai 40 detik. Hasilnya berupa draf yang wajib Anda periksa.</p>
      </form>
    </section>
  );
}

/** Fields two per row from 640px (one per row below), so no control grows past the 640px form cap. */
function FieldGrid({ nodes }: { nodes: ReactNode[] }) {
  const rows: ReactNode[][] = [];
  for (let i = 0; i < nodes.length; i += 2) rows.push(nodes.slice(i, i + 2));
  return (
    <>
      {rows.map((r, i) => (
        <div className="field-row" key={i}>
          {r}
        </div>
      ))}
    </>
  );
}

export function DynamicFields({
  fields,
  values,
  errors,
  onChange,
  idPrefix,
}: {
  fields: API.CatalogField[];
  values: Record<string, string>;
  errors: Record<string, string>;
  onChange: (key: string, value: string) => void;
  /** Ids are `${idPrefix}${key}`; a screen with several forms gives each its own prefix. */
  idPrefix?: string;
}) {
  return (
    <FieldGrid
      nodes={fields.map((f) => (
        <DynamicField key={f.key} field={f} value={values[f.key] ?? ""} error={errors[f.key]} onChange={(v) => onChange(f.key, v)} idPrefix={idPrefix} />
      ))}
    />
  );
}

export function DynamicField({
  field,
  value,
  error,
  onChange,
  idPrefix = "f-",
}: {
  field: API.CatalogField;
  value: string;
  error?: string;
  onChange: (v: string) => void;
  idPrefix?: string;
}) {
  const id = `${idPrefix}${field.key}`;
  const aria = { ...fieldAria(id, error), required: field.required || undefined, name: field.key };
  let control: ReactNode;
  let hint: ReactNode = null;
  switch (field.type) {
    case "textarea":
      control = <textarea {...aria} rows={4} value={value} placeholder={field.placeholder} onChange={(e) => onChange(e.currentTarget.value)} />;
      break;
    case "number":
      control = <input {...aria} type="text" inputMode="numeric" value={value} placeholder={field.placeholder} onChange={(e) => onChange(digitsOnly(e.currentTarget.value))} />;
      hint = value ? `Terbaca: ${Number(value).toLocaleString("id-ID")}` : "Angka saja, tanpa titik.";
      break;
    case "date":
      control = <input {...aria} type="date" value={value} onChange={(e) => onChange(e.currentTarget.value)} />;
      break;
    case "select":
      control = (
        <select {...aria} value={value} onChange={(e) => onChange(e.currentTarget.value)}>
          <option value="">{field.placeholder ?? "Pilih"}</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
      break;
    default:
      control = <input {...aria} type="text" value={value} placeholder={field.placeholder} onChange={(e) => onChange(e.currentTarget.value)} />;
  }
  return (
    <Field id={id} label={field.label} required={field.required} error={error} hint={hint}>
      {control}
    </Field>
  );
}
